import type { PropsWithChildren } from 'react';
import React from 'react';

import Box from '@mui/material/Box';

import Image from 'src/components/ui/Image';

import { breakpoints } from 'src/constants/breakpoints';
import { noImagePath } from 'src/constants/defaultImages';
import { styledComponent, styledTag } from 'src/utils/styled';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { AnalyticsEvent } from 'src/utils/analytics';
import useBreakpoints from 'src/hooks/useBreakpoints';

const Container = styledTag('div')`
    display: flex;
    flex-direction: row;
    height: 32rem;
    position: relative;
    border-radius: 1rem;
    gap: 0.25rem;

`;

const StyledHeaderImage = styledComponent(Box)<{
    width: string;
    height: string;
    pointer?: boolean;
}>`
    height: ${({ height }) => height};
    width: ${({ width }) => width};
    position: relative;
    ${({ pointer }) => pointer && `cursor: pointer`};
    @media screen and (min-width: ${breakpoints.tabletPortrait}px) {
        padding: 0.156rem;
    }
`;

interface HeaderImageProps {
    width: string;
    height: string;
    src: string;
    open?: (src?: string) => void;
}

const HeaderImage: React.FC<HeaderImageProps> = ({ width, height, src, open }) => {
    const { logEventDynamic } = useDynamicLogEvents();
    const handleClick = () => {
        open?.(src);
        logEventDynamic(AnalyticsEvent.SeePictures);
    };
    return (
        <StyledHeaderImage width={width} height={height} onClick={handleClick} pointer>
            <Image
                src={src}
                alt="ad image"
                style={{
                    objectFit: 'cover',
                    backgroundColor: 'transparent',
                }}
                fill
            />
        </StyledHeaderImage>
    );
};

const VerticalImages = styledTag('div')<{
    width: string;
}>`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: ${({ width }) => width};
    gap: 0.25rem;
`;

type Props = {
    images: Array<{ url: string } | null>;
    open?: (src?: string) => void;
};

const AdGallery: React.FC<PropsWithChildren<Props>> = ({ images, open, children }) => {
    const { isMobile } = useBreakpoints();

    let content;
    if (!images.length) {
        content = (
            <StyledHeaderImage width="100%" height="100%">
                {' '}
                <Image
                    src={noImagePath}
                    alt="ad image"
                    style={{
                        objectFit: 'cover',
                        padding: '0.156rem',
                        backgroundColor: 'transparent',
                    }}
                    fill
                />
            </StyledHeaderImage>
        );
    } else if (isMobile) {
        content = <HeaderImage width="100%" height="100%" src={images[0]?.url ?? ''} open={open} />;
    } else {
        switch (images.length) {
            case 1:
                content = <HeaderImage width="100%" height="100%" src={images[0]?.url ?? ''} open={open} />;
                break;
            case 2:
                content = (
                    <>
                        <HeaderImage width="50%" height="100%" src={images[0]?.url ?? ''} open={open} />
                        <HeaderImage width="50%" height="100%" src={images[1]?.url ?? ''} open={open} />
                    </>
                );
                break;
            case 3:
            case 4:
                content = (
                    <>
                        <HeaderImage width="50%" height="100%" src={images[0]?.url ?? ''} open={open} />
                        <VerticalImages width="50%">
                            <HeaderImage width="100%" height="50%" src={images[1]?.url ?? ''} open={open} />
                            <HeaderImage width="100%" height="50%" src={images[2]?.url ?? ''} open={open} />
                        </VerticalImages>
                    </>
                );
                break;
            default:
                content = (
                    <>
                        <HeaderImage width="50%" height="100%" src={images[0]?.url ?? ''} open={open} />
                        <VerticalImages width="25%">
                            <HeaderImage width="100%" height="50%" src={images[1]?.url ?? ''} open={open} />
                            <HeaderImage width="100%" height="50%" src={images[2]?.url ?? ''} open={open} />
                        </VerticalImages>
                        <VerticalImages width="25%">
                            <HeaderImage width="100%" height="50%" src={images[3]?.url ?? ''} open={open} />
                            <HeaderImage width="100%" height="50%" src={images[4]?.url ?? ''} open={open} />
                        </VerticalImages>
                    </>
                );
                break;
        }
    }

    return (
        <Container>
            {content}
            {children}
        </Container>
    );
};

export default AdGallery;
